import React, { useMemo } from "react";
import {
  Box,
  Badge,
  Heading,
  Text,
  VStack,
  HStack,
  Flex,
  useColorModeValue,
  Tooltip,
  SimpleGrid,
} from "@chakra-ui/react";
import { MdBatteryStd } from "react-icons/md";
import { FiDroplet } from "react-icons/fi";
import { WiHumidity } from "react-icons/wi";
import { BsSun } from "react-icons/bs";
import { FaThermometerHalf, FaBatteryHalf, FaTint, FaSun, FaTachometerAlt } from "react-icons/fa";
import { Trans } from "@lingui/react/macro";
import { IDevice } from "../../types/deviceInterface";

const deviceTypeLabel = new Map<number, string>([
  [1, "SENSOR"],
  [2, "AUTOPLANT"],
  [3, "ENV"],
  [4, "RELAY"],
  [5, "VALVE"],
  [6, "FLOW"],
  [7, "PUMP"],
  [9, "TRACKER"],
]);

const formatTimestamp = (iso: string | undefined) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("default", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const timeAgo = (iso?: string) => {
  if (!iso) return "";
  const now = Date.now();
  const ts = new Date(iso).getTime();
  const diff = Math.max(0, now - ts);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const Metric = ({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
}) => {
  const chipBg = useColorModeValue("blackAlpha.50", "whiteAlpha.100");
  const chipBorder = useColorModeValue("blackAlpha.200", "whiteAlpha.300");
  return (
    <HStack
      spacing={2}
      borderWidth="1px"
      px={2}
      py={1}
      rounded="md"
      w="100%"
      maxW="full"
      minW={0}
      overflow="hidden"
    >
      <Box as={Icon} boxSize={4} flexShrink={0} />
      <Text fontSize="sm" noOfLines={1} flexShrink={0}>
        {label}:
      </Text>
      <Text fontWeight="semibold" fontSize="sm" flex="1" textAlign="right">
        {value}
      </Text>
    </HStack>
  );
};

const DeviceWidget = ({ device }: { device: IDevice }) => {
  const cardBg = useColorModeValue("white", "gray.900");
  const textColor = useColorModeValue("gray.800", "gray.200");
  const subtle = useColorModeValue("gray.600", "gray.400");

  const t = deviceTypeLabel.get(device.device_type) || "UNKNOWN";
  const d = device.data || ({} as any);
  const lastTs = d?.timestamp as string | undefined;
  const isTracker = device.device_type === 9;

  const metrics = useMemo(() => {
    const out: { icon: React.ElementType; label: string; value: string }[] = [];

    const sm = d.soil_moisture as number | undefined;
    if (!isTracker && typeof sm === "number")
      out.push({ icon: FiDroplet, label: "Soil", value: `${sm.toFixed(1)} %` });

    const sm2 = d.soil_moisture_2 as number | undefined;
    if (!isTracker && typeof sm2 === "number")
      out.push({ icon: FiDroplet, label: "Soil 2", value: `${sm2.toFixed(1)} %` });

    const sm3 = d.soil_moisture_3 as number | undefined;
    if (!isTracker && typeof sm3 === "number")
      out.push({ icon: FiDroplet, label: "Soil 3", value: `${sm3.toFixed(1)} %` });

    const st = d.soil_temperature as number | undefined;
    if (!isTracker && typeof st === "number")
      out.push({ icon: FaThermometerHalf, label: "Soil °C", value: st.toFixed(1) });

    const at = d.air_temperature as number | undefined;
    if (typeof at === "number")
      out.push({ icon: FaThermometerHalf, label: "Air °C", value: at.toFixed(1) });

    const h = d.humidity as number | undefined;
    if (typeof h === "number")
      out.push({ icon: WiHumidity, label: "Humidity", value: `${h.toFixed(0)} %` });

    const lx = d.lux as number | undefined;
    if (typeof lx === "number") out.push({ icon: BsSun, label: "Lux", value: lx.toFixed(0) });

    const sc = d.soil_conductivity as number | undefined;
    if (!isTracker && typeof sc === "number")
      out.push({ icon: FaTachometerAlt, label: "Cond.", value: sc.toFixed(0) });

    const sc2 = d.soil_conductivity_2 as number | undefined;
    if (!isTracker && typeof sc2 === "number")
      out.push({ icon: FaTachometerAlt, label: "Cond. 2", value: sc2.toFixed(0) });

    const sc3 = d.soil_conductivity_3 as number | undefined;
    if (!isTracker && typeof sc3 === "number")
      out.push({ icon: FaTachometerAlt, label: "Cond. 3", value: sc3.toFixed(0) });

    const bv = d.battery_voltage as number | undefined;
    if (typeof bv === "number")
      out.push({ icon: MdBatteryStd, label: "Battery", value: `${bv.toFixed(2)} V` });

    const b = d.battery as number | undefined;
    if (typeof b === "number" && bv === undefined)
      out.push({ icon: MdBatteryStd, label: "Battery", value: `${b.toFixed(2)} V` });

    return out;
  }, [d, isTracker]);

  return (
    <Box
      w="100%"
      maxW={{ base: "100%", sm: "420px" }}
      minH="auto"
      borderRadius="xl"
      p={4}
      boxShadow="lg"
      bg={cardBg}
      color={textColor}
      transition="all 0.15s ease"
      _hover={{ boxShadow: "xl", transform: "translateY(-2px)" }}
    >
      <VStack align="stretch" spacing={3}>
        <Flex align="center" justify="space-between" gap={3}>
          <Heading size="md" noOfLines={1}>
            {device.device_name || (
              <>
                <Trans>Device</Trans>
                {" #"}
                {device?.device_id}
              </>
            )}
          </Heading>
          <Badge colorScheme="green" whiteSpace="nowrap">
            {t}
          </Badge>
        </Flex>

        {metrics.length > 0 ? (
          <SimpleGrid
            templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }}
            gap={2}
            alignItems="stretch"
          >
            {metrics.map((m, idx) => (
              <Metric key={idx} icon={m.icon} label={m.label} value={m.value} />
            ))}
          </SimpleGrid>
        ) : (
          <Text color={subtle} fontSize="sm">
            <Trans>Current data unavailable</Trans>
          </Text>
        )}

        <HStack justify="space-between" pt={1}>
          <Text fontSize="sm" color={subtle} noOfLines={1}>
            ID: {device.device_id}
          </Text>
          <Tooltip label={formatTimestamp(lastTs)} hasArrow>
            <Text fontSize="sm" color={subtle}>
              {timeAgo(lastTs)}
            </Text>
          </Tooltip>
        </HStack>
      </VStack>
    </Box>
  );
};

export default DeviceWidget;
